import {
  GraphQLObjectType,
  GraphQLString,
  GraphQLID,
  GraphQLNonNull,
  GraphQLEnumType,
  GraphQLList,
  GraphQLBoolean
} from 'graphql'
import { dateTimeType, DateTime } from './date-time-type'
// @ts-ignore todo TS-3.8.3 - TS-8508
import { VulnerabilityStatus } from '../shared'
import {
  ApplicationImportance,
  ApplicationImportanceType
} from './application-importance'
import { role, Role } from './role'
import { GQLBugTracker, bugTrackerType } from './bug-trackers'

// GraphQL cannot resolve application language to AgentLanguage, therefore a seperate interface is required.
export interface GQLVulnerabilityApplication {
  readonly id: string
  readonly name: string
  readonly url: string
  readonly importance: ApplicationImportance
  readonly licensed: boolean
  readonly language: string
  readonly role: Role
  readonly parentApplicationId?: string
  readonly isParentApp?: boolean
}

export type VulnerabilitySeverity =
  | 'NOTE'
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH'
  | 'CRITICAL'

export interface PolicyViolation {
  readonly name: string
  readonly id: string
}

export interface PendingStatus {
  readonly status: VulnerabilityStatus
  readonly created: string
}

export interface Session {
  readonly sessionID: string
  readonly metadata: SessionMetadata[]
}

interface SessionMetadata {
  readonly agentLabel: string
  readonly displayLabel: string
  readonly value: string
}

export interface GQLVulnerability {
  readonly id: string
  readonly title: string
  readonly ruleName: string
  readonly visible: boolean
  readonly application: GQLVulnerabilityApplication
  readonly severity: VulnerabilitySeverity
  readonly defaultSeverity: VulnerabilitySeverity
  readonly tags: string[]
  readonly lastDetected: DateTime
  readonly firstDetected: DateTime
  readonly status: VulnerabilityStatus
  readonly substatus: string | null
  readonly reportedToBugTracker: boolean
  readonly reportedToBugTrackerTime: DateTime | null
  readonly bugTrackers: GQLBugTracker[]
  readonly violations: PolicyViolation[]
  readonly pendingStatus: PendingStatus | null
  readonly sessions: Session[]
  readonly protectStatus?: string
}

export const vulnerabilityApplicationType = new GraphQLObjectType({
  name: 'VulnerabilityApplication',
  fields: () => ({
    id: {
      description:
        'The unique ID for the application where this vulnerability was detected',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'The name of the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    url: {
      description: 'Application URL',
      type: new GraphQLNonNull(GraphQLString)
    },
    importance: {
      description: 'Application Importance',
      type: new GraphQLNonNull(ApplicationImportanceType)
    },
    licensed: {
      description: 'Indicates if this application is licensed',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    language: {
      description: 'Application language',
      type: new GraphQLNonNull(GraphQLString)
    },
    role: {
      description: 'Application role',
      type: new GraphQLNonNull(role)
    },
    parentApplicationId: {
      description: "ID of the application's parent if it has one",
      type: GraphQLString
    },
    isParentApp: {
      description: 'Indicates if this app is a parent of a merge',
      type: GraphQLBoolean
    }
  })
})

export const vulnerabilitySeverityType = new GraphQLEnumType({
  name: 'VulnerabilitySeverity',
  values: {
    NOTE: {
      value: 'NOTE'
    },
    LOW: {
      value: 'LOW'
    },
    MEDIUM: {
      value: 'MEDIUM'
    },
    HIGH: {
      value: 'HIGH'
    },
    CRITICAL: {
      value: 'CRITICAL'
    }
  }
})

export const vulnerabilityStatusType = new GraphQLEnumType({
  name: 'VulnerabilityStatus',
  values: {
    Reported: {
      value: 'Reported'
    },
    Suspicious: {
      value: 'Suspicious'
    },
    Confirmed: {
      value: 'Confirmed'
    },
    NotAProblem: {
      value: 'NotAProblem'
    },
    Remediated: {
      value: 'Remediated'
    },
    Fixed: {
      value: 'Fixed'
    },
    AutoRemediated: {
      value: 'AutoRemediated'
    }
  }
})

const policyViolationType = new GraphQLObjectType({
  name: 'PolicyViolation',
  fields: () => ({
    id: {
      description: 'The unique ID for a policy violation',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Policy violation name',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export const pendingStatusType = new GraphQLObjectType({
  name: 'PendingStatus',
  fields: () => ({
    status: {
      description: 'Vulnerability Status',
      type: new GraphQLNonNull(vulnerabilityStatusType)
    },
    created: {
      description: 'Created Date/Time',
      type: new GraphQLNonNull(dateTimeType)
    }
  })
})

const sessionMetadataType = new GraphQLObjectType({
  name: 'SessionMetadata',
  fields: () => ({
    agentLabel: {
      description: 'Session Metadata field agent label',
      type: new GraphQLNonNull(GraphQLString)
    },
    displayLabel: {
      description: 'Session Metadata field display label',
      type: new GraphQLNonNull(GraphQLString)
    },
    value: {
      description: 'Session Metadata field value',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export const sessionType = new GraphQLObjectType({
  name: 'Session',
  fields: () => ({
    sessionID: {
      description: 'Session ID',
      type: new GraphQLNonNull(GraphQLID)
    },
    metadata: {
      description: 'Session Metadata fields',
      type: new GraphQLList(new GraphQLNonNull(sessionMetadataType))
    }
  })
})

export const vulnerabilityType = new GraphQLObjectType({
  name: 'Vulnerability',
  fields: () => ({
    id: {
      description: 'The unique ID for a vulnerability',
      type: new GraphQLNonNull(GraphQLID)
    },
    title: {
      description: 'Main description of the vulnerability',
      type: new GraphQLNonNull(GraphQLString)
    },
    ruleName: {
      description: 'The name of the rule this vulnerability was discovered by',
      type: new GraphQLNonNull(GraphQLString)
    },
    visible: {
      description:
        'Whether or not the vulnerability is owned by a licensed application',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    application: {
      description: 'Application',
      type: vulnerabilityApplicationType
    },
    severity: {
      description: 'Severity',
      type: new GraphQLNonNull(vulnerabilitySeverityType)
    },
    defaultSeverity: {
      description: 'Default Severity of the associated rule',
      type: new GraphQLNonNull(vulnerabilitySeverityType)
    },
    tags: {
      description: 'User defined tags',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    lastDetected: {
      description: 'Last time detected',
      type: new GraphQLNonNull(dateTimeType)
    },
    firstDetected: {
      description: 'First time detected',
      type: new GraphQLNonNull(dateTimeType)
    },
    status: {
      description: 'Status',
      type: new GraphQLNonNull(vulnerabilityStatusType)
    },
    substatus: {
      description: 'The reason provided for marking as "Not a Problem"',
      type: GraphQLString
    },
    reportedToBugTracker: {
      description: 'Has the vulnerability been reported to a bug tracker?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    reportedToBugTrackerTime: {
      description: 'When this vulnerability was reported to a bug tracker',
      type: dateTimeType
    },
    bugTrackers: {
      description: 'Bug Trackers',
      type: new GraphQLList(bugTrackerType)
    },
    violations: {
      description: 'Policy violations',
      type: new GraphQLNonNull(new GraphQLList(policyViolationType))
    },
    pendingStatus: {
      description:
        'Indicates if a vulnerability status change is pending admin approval',
      type: pendingStatusType
    },
    sessions: {
      description: 'Sessions that belong to the given vulnerability',
      type: new GraphQLList(new GraphQLNonNull(sessionType))
    },
    protectStatus: {
      description: 'Status of vuln within protect',
      type: GraphQLString
    }
  })
})
